import React, {useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import Protected from './Protected';
import {ProtectedFieldSet, useAuthDisabled} from '../context/AuthDisabledContext';

/**
 * Reusable Pattern-B (inline view/edit) card shell.
 *
 * Composition contract (clean separation — NO hidden behaviour):
 *
 *   <EditableCard
 *     title="Tech Profile"
 *     protectedBy={MachineTechProfileCard}     // component with .abilities
 *     protectAction="edit"                      // ability key
 *     value={machine.techProfile}               // current value (any shape)
 *     save={(draft) => apiService.put(URL, draft)}    // ⇒ Promise<UpdatedParent>
 *     onSaved={(updatedParent) => ...}          // called with save's resolved value
 *     renderView={(value) => <ReadOnlyJsx/>}    // pure renderer
 *     renderForm={(draft, setDraft) => <FormJsx/>}   // pure renderer, controlled
 *   />
 *
 * Card-internal state (editing flag, draft, saving, error, toast) is fully
 * encapsulated here. Callers provide ONLY:
 *
 *   - title, protectedBy/protectAction — declarative
 *   - value, save, onSaved             — data + persistence
 *   - renderView, renderForm           — UI slots
 *
 * Migration to Pattern A (single page edits the whole machine) doesn't
 * touch this component: the FORMS rendered inside renderForm are pure
 * controlled inputs, so a page-level form can mount them directly without
 * EditableCard at all.
 */
const EditableCard = ({
    title,
    protectedBy,
    protectAction,
    value,
    save,
    onSaved,
    renderView,
    renderForm,
    successMessage = 'Saved',
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    const startEdit = () => {
        setDraft(value);
        setError(null);
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setError(null);
    };

    const submit = async (e) => {
        e?.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const updated = await save(draft);
            onSaved?.(updated);
            setToast(successMessage);
            setEditing(false);
        } catch (err) {
            setError(err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper elevation={2} sx={{p: 2, height: '100%'}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
                <Typography variant="h6">{title}</Typography>
                {!editing && (
                    <Protected component={protectedBy} action={protectAction}>
                        <Button size="small" startIcon={<EditIcon/>} onClick={startEdit}>Edit</Button>
                    </Protected>
                )}
            </Stack>
            <Divider sx={{mb: 2}}/>

            {editing ? (
                <Protected component={protectedBy} action={protectAction} mode="disable">
                    <ProtectedFieldSet>
                        <Box component="form" onSubmit={submit}>
                            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                            {renderForm(draft, setDraft)}
                            <EditActionButtons saving={saving} onCancel={cancel}/>
                        </Box>
                    </ProtectedFieldSet>
                </Protected>
            ) : (
                renderView(value)
            )}

            <Snackbar open={!!toast} autoHideDuration={3000}
                      onClose={() => setToast(null)} message={toast}/>
        </Paper>
    );
};

const EditActionButtons = ({saving, onCancel}) => {
    const isDisabled = useAuthDisabled();
    return (
        <Stack direction="row" spacing={1} sx={{mt: 2}}>
            <Button type="submit" variant="contained"
                    startIcon={saving ? <CircularProgress size={16}/> : <SaveIcon/>}
                    disabled={saving || isDisabled}>
                Save
            </Button>
            <Button onClick={onCancel} startIcon={<CloseIcon/>} disabled={saving}>
                Cancel
            </Button>
        </Stack>
    );
};

export default EditableCard;
